import { useState } from "react"
import { useTranslation } from "react-i18next"
import { MdAddCircleOutline, MdPhotoCamera } from "react-icons/md"
import { Box, Flex, IconButton, Input, Spinner, Text } from "@chakra-ui/react"
import { InputGroup } from "../../ui/input-group"
import BarcodeScanner from "../../common/BarcodeScanner"
import { NeutralButton } from "../../common/Buttons"
import { colors } from "../../../utilities/colors"
import useStore from "../../../store"

export default function RequestTransportTile({ onAddVehicle }) {
  const { t } = useTranslation()
  const { decodeVin } = useStore()

  const [vin, setVin] = useState('')
  const [isDecodingVin, setIsDecodingVin] = useState(false)
  const [decodedVehicle, setDecodedVehicle] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [openCamera, setOpenCamera] = useState(false)

  const handleDecode = async (vinToCheck) => {
    setIsDecodingVin(true)
    setDecodedVehicle(null)
    setErrorMessage(null)
    const result = await decodeVin(vinToCheck)
    setIsDecodingVin(false)
    if (result?.year && result?.make && result?.model) {
      setDecodedVehicle({ ...result, vin: vinToCheck })
    } else {
      setErrorMessage(t('Could not find a vehicle for that VIN. Please check and try again.'))
    }
  }

  const handleScan = (scanned) => {
    setVin(scanned)
    handleDecode(scanned)
  }

  const handleVinChange = (e) => {
    const val = e.target.value.toUpperCase()
    setVin(val)
    setDecodedVehicle(null)
    setErrorMessage(null)
  }

  const handleAddVehicle = () => {
    if (!decodedVehicle) return
    const shaped = {
      id: decodedVehicle.vin,
      attributes: {
        vin: decodedVehicle.vin,
        year: decodedVehicle.year,
        make: decodedVehicle.make,
        model: decodedVehicle.model,
        color: null,
      },
    }
    onAddVehicle?.(shaped)
    setVin('')
    setDecodedVehicle(null)
    setErrorMessage(null)
  }

  const vehicleLabel = (vehicle) =>
    [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(' ')

  return (
    <>
      <Box
        w="full"
        pb={3}
        px={1}
        display="flex"
        flexDirection="column"
        gap={3}
        borderBottom="2px solid"
        borderColor={colors.grey700}
      >
        <InputGroup
          w="full"
          endElement={
            <MdPhotoCamera
              data-testid="camera-icon"
              style={{ cursor: 'pointer', fontSize: '1.25rem', color: colors.grey300 }}
              onClick={() => setOpenCamera(true)}
            />
          }
        >
          <Input
            value={vin}
            aria-label="vin"
            name="vin"
            placeholder={t('Enter VIN for External Inventory')}
            onChange={handleVinChange}
            autoComplete="off"
            h={7}
            px={3}
            py={0.5}
            bg={colors.grey900}
            color={colors.grey100}
            borderWidth="1px"
            borderColor={colors.grey}
            borderRadius="full"
            _placeholder={{ color: colors.grey }}
            _focus={{ borderColor: colors.grey100, boxShadow: 'none', outline: 'none' }}
            _focusVisible={{ borderColor: colors.grey100, boxShadow: 'none', outline: 'none' }}
          />
        </InputGroup>

        {vin.length === 17 && !decodedVehicle && !isDecodingVin && (
          <NeutralButton
            text={t('Confirm VIN')}
            onClick={() => handleDecode(vin)}
          />
        )}

        {isDecodingVin && (
          <Box display="flex" alignItems="center" gap={2} color={colors.grey300}>
            <Spinner size="sm" />
            <Text fontSize="sm">{t('Looking up vehicle...')}</Text>
          </Box>
        )}

        {errorMessage && (
          <Box
            p={3}
            borderRadius="md"
            border="1px solid"
            borderColor={colors.fpxRed}
            bg={`${colors.fpxRed}18`}
          >
            <Text fontSize="sm" color={colors.fpxRed}>
              {errorMessage}
            </Text>
          </Box>
        )}

        {decodedVehicle && (
          <Flex
            p={4}
            borderRadius="md"
            border="1px solid"
            borderColor={colors.green}
            bg={`${colors.green}18`}
            alignItems="center"
            gap={3}
          >
            <Box flex={1} minW={0}>
              <Text fontSize="md" fontWeight="semibold" color={colors.grey100}>
                {vehicleLabel(decodedVehicle)}
              </Text>
              <Text fontSize="xs" color={colors.grey300} mt={1}>
                {decodedVehicle.vin}
              </Text>
            </Box>
            <IconButton
              aria-label={t('Add searched vehicle to quote')}
              onClick={handleAddVehicle}
              variant="ghost"
              size="lg"
              color={colors.green}
              _hover={{ bg: `${colors.green}20` }}
            >
              <MdAddCircleOutline size={48} />
            </IconButton>
          </Flex>
        )}
      </Box>

      <div className="fixed z-50 top-0 right-0">
        {openCamera && (
          <BarcodeScanner
            close={() => setOpenCamera(false)}
            setBarcode={handleScan}
          />
        )}
      </div>
    </>
  )
}
